import React, { useState, useEffect } from "react";
import {
  Card,
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";
import {
  Add,
  Save,
  Delete,
  Edit,
  Refresh,
  PlayArrow,
  Close,
} from "@material-ui/icons";
import { useDispatch } from "react-redux";

import { apiCall } from "../../utils/api";
import { API_URLS } from "../../constants/api";
import { notification } from "../../reducer/home/action";

// NotificationRules — admin CRUD bảng `notification_rules`.
// Mỗi rule = 1 mốc nhắc (D-X / D+X) cho 1 loại deadline.
// Route: /admin/notification-rules (xem AppLayout).

const KIND_OPTIONS = [
  { value: "sr_end_time",        label: "SV — Kết thúc đề tài" },
  { value: "ir_end_time",        label: "Cơ sở — Kết thúc đề tài" },
  { value: "sr_bctd_1",          label: "SV — Hạn BCTĐ" },
  { value: "ir_bctd",            label: "Cơ sở — Hạn BCTĐ (2y)" },
  { value: "sr_acceptance",      label: "SV — Hạn nghiệm thu" },
  { value: "ir_acceptance",      label: "Cơ sở — Hạn nộp tổng kết" },
  { value: "sr_reg_pending",     label: "SV — Đăng ký chờ duyệt quá lâu" },
  { value: "ir_reg_pending",     label: "Cơ sở — Đăng ký chờ duyệt quá lâu" },
  { value: "sr_council_pending", label: "SV — HĐ chưa có kết quả" },
  { value: "ir_council_pending", label: "Cơ sở — HĐ chưa có kết quả" },
];

const RECIPIENT_OPTIONS = [
  { value: "personal",   label: "Chủ nhiệm" },
  { value: "instructor", label: "GV/CB hướng dẫn" },
  { value: "mod",        label: "Trưởng đơn vị / khoa" },
  { value: "admin",      label: "Phòng QLKH" },
];

const labelOfKind = (k) => KIND_OPTIONS.find((o) => o.value === k)?.label || k;

const labelOfRecipients = (csv) => {
  if (!csv) return "—";
  return csv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((k) => RECIPIENT_OPTIONS.find((o) => o.value === k)?.label || k)
    .join(", ");
};

function emptyRule() {
  return {
    id: 0,
    kind: "sr_end_time",
    leadDays: 7,
    recipients: "personal,instructor",
    active: true,
    description: "",
  };
}

export default function NotificationRules() {
  const dispatch = useDispatch();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = đóng modal; object = đang edit
  const [saving, setSaving] = useState(false);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const api = API_URLS.NOTIFICATION_RULE.listRules();
      const { response, error } = await apiCall({ ...api });
      if (!error && response?.status === 200 && response.data.success) {
        setRules(response.data.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const api = editing.id > 0
        ? API_URLS.NOTIFICATION_RULE.updateRule(editing.id)
        : API_URLS.NOTIFICATION_RULE.createRule();
      const { response, error } = await apiCall({ ...api, payload: editing });
      if (!error && response?.status === 200 && response.data.success) {
        dispatch(notification({ type: "success", message: "Đã lưu rule." }));
        setEditing(null);
        fetchRules();
      } else {
        const msg = error?.response?.data?.message || response?.data?.message || "Lưu thất bại.";
        dispatch(notification({ type: "error", message: msg }));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Xoá rule này?")) return;
    const api = API_URLS.NOTIFICATION_RULE.deleteRule(id);
    const { response, error } = await apiCall({ ...api });
    if (!error && response?.status === 200 && response.data.success) {
      dispatch(notification({ type: "success", message: "Đã xoá rule." }));
      fetchRules();
    }
  };

  const handleRunNow = async () => {
    const api = API_URLS.NOTIFICATION_RULE.runNow();
    const { response, error } = await apiCall({ ...api });
    if (!error && response?.status === 200) {
      dispatch(notification({
        type: "info",
        message: "Đã trigger job nhắc deadline. Xem kết quả ở icon notification (vài giây).",
      }));
    }
  };

  return (
    <Card
      style={{
        padding: "16px 24px",
        margin: 16,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item xs={6}>
          <Typography variant="h5"><b>Cấu hình nhắc deadline đề tài</b></Typography>
          <Typography variant="caption" color="textSecondary">
            Quản lý các mốc cảnh báo (D-X / D+X) cho từng loại deadline. Cron daily 6h sáng sẽ
            đọc rules active và sinh notification cho người dùng.
          </Typography>
        </Grid>
        <Grid item xs={6} style={{ textAlign: "right" }}>
          <Tooltip title="Tải lại">
            <IconButton onClick={fetchRules}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined" color="primary"
            startIcon={<PlayArrow />} style={{ marginLeft: 8 }}
            onClick={handleRunNow}
          >
            Chạy job ngay
          </Button>
          <Button
            variant="contained" color="primary"
            startIcon={<Add />} style={{ marginLeft: 8 }}
            onClick={() => setEditing(emptyRule())}
          >
            Thêm rule
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" padding={3}>
          <CircularProgress />
        </Box>
      ) : (
        // Refactor 2026-05-21: wrap toàn bộ "table" trong 1 Box maxHeight +
        // overflowY=auto để khi list dài (25+ rule default) bảng tự scroll
        // trong khung, không đẩy AppFooter ra khỏi viewport. Header sticky để
        // luôn nhìn thấy tên cột khi scroll.
        <Box
          border="solid 1px #e0e0e0"
          borderRadius={4}
          bgcolor="#fafafa"
          style={{
            maxHeight: "calc(100vh - 260px)",
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          <Box
            display="flex"
            padding="10px 16px"
            borderBottom="solid 1px #e0e0e0"
            bgcolor="#f0f0f0"
            style={{
              fontWeight: 600,
              fontSize: 13,
              position: "sticky",
              top: 0,
              zIndex: 1,
              minWidth: 920,
            }}
          >
            <Box flex={3} minWidth={240}>Loại deadline</Box>
            <Box width={100} flexShrink={0} textAlign="center">D-Day</Box>
            <Box flex={3} minWidth={220}>Người nhận</Box>
            <Box flex={2} minWidth={180}>Mô tả</Box>
            <Box width={80}  flexShrink={0} textAlign="center">Active</Box>
            <Box width={100} flexShrink={0} textAlign="center">Hành động</Box>
          </Box>
          {rules.length === 0 && (
            <Box padding={3} textAlign="center">
              <Typography variant="caption" color="textSecondary">
                Chưa có rule nào. Bấm "Thêm rule" để tạo mới.
              </Typography>
            </Box>
          )}
          {rules.map((r) => (
            <Box
              key={r.id}
              display="flex" alignItems="center"
              padding="10px 16px"
              borderBottom="solid 1px #e8e8e8"
              bgcolor="white"
              style={{ fontSize: 13, minWidth: 920 }}
            >
              <Box flex={3} minWidth={240}>{labelOfKind(r.kind)}</Box>
              <Box width={100} flexShrink={0} textAlign="center">
                {r.leadDays > 0 ? `D-${r.leadDays}` : r.leadDays === 0 ? "D-day" : `D+${-r.leadDays}`}
              </Box>
              <Box flex={3} minWidth={220} style={{ fontSize: 12 }}>{labelOfRecipients(r.recipients)}</Box>
              <Box flex={2} minWidth={180} style={{ fontSize: 12, color: "#666" }}>{r.description || "—"}</Box>
              <Box width={80} flexShrink={0} textAlign="center">
                <span style={{ color: r.active ? "#4caf50" : "#9e9e9e" }}>{r.active ? "ON" : "OFF"}</span>
              </Box>
              <Box width={100} flexShrink={0} textAlign="center">
                <Tooltip title="Sửa">
                  <IconButton size="small" onClick={() => setEditing({ ...r })}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Xoá">
                  <IconButton size="small" onClick={() => handleDelete(r.id)}>
                    <Delete fontSize="small" style={{ color: "#f44336" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Edit / Create modal */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing?.id > 0 ? "Sửa rule" : "Thêm rule mới"}
        </DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" style={{ marginBottom: 6 }}>
                  <b>Loại deadline</b>
                </Typography>
                <Select
                  fullWidth variant="outlined" margin="dense"
                  value={editing.kind}
                  onChange={(e) => setEditing({ ...editing, kind: e.target.value })}
                >
                  {KIND_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" style={{ marginBottom: 6 }}>
                  <b>Số ngày</b>
                  <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                    Dương = trước deadline; Âm = sau (quá hạn); 0 = đúng ngày.
                  </Typography>
                </Typography>
                <TextField
                  fullWidth size="small" variant="outlined" type="number"
                  value={editing.leadDays}
                  onChange={(e) => setEditing({ ...editing, leadDays: parseInt(e.target.value, 10) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editing.active}
                      onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Active"
                  style={{ marginTop: 24 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" style={{ marginBottom: 6 }}>
                  <b>Người nhận</b>
                </Typography>
                <Box display="flex" flexWrap="wrap" gridGap={8}>
                  {RECIPIENT_OPTIONS.map((o) => {
                    const current = (editing.recipients || "").split(",").map((s) => s.trim());
                    const checked = current.includes(o.value);
                    return (
                      <FormControlLabel
                        key={o.value}
                        control={
                          <Switch
                            size="small"
                            checked={checked}
                            onChange={(e) => {
                              const set = new Set(current.filter(Boolean));
                              if (e.target.checked) set.add(o.value); else set.delete(o.value);
                              setEditing({ ...editing, recipients: Array.from(set).join(",") });
                            }}
                            color="primary"
                          />
                        }
                        label={o.label}
                      />
                    );
                  })}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" style={{ marginBottom: 6 }}>
                  <b>Mô tả</b>
                </Typography>
                <TextField
                  fullWidth size="small" variant="outlined" multiline minRows={2}
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Close />} onClick={() => setEditing(null)}>Đóng</Button>
          <Button
            color="primary" variant="contained" startIcon={<Save />}
            disabled={saving} onClick={handleSave}
          >
            Lưu
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
